/**
 * Newsletter Drawer Web Component
 * Displays newsletter signup with Klaviyo integration
 * Triggers on exit intent or time delay
 */

class NewsletterDrawer extends HTMLElement {
  constructor() {
    super();
    
    // Configuration
    this.KLAVIYO_API_KEY = 'XyNQWJ';
    this.KLAVIYO_LIST_ID = 'Y5LT7N'; // Using the provided key as list ID
    this.COMPANY_NAME = 'Stephen Webster';
    this.COOKIE_NAME = 'newsletter_drawer_closed';
    
    // Settings from data attributes
    this.delay = parseInt(this.dataset.delay || '10') * 1000;
    this.exitIntent = this.dataset.exitIntent === 'true';
    this.cookieDays = parseInt(this.dataset.cookieDays || '30');
    
    // State
    this.isOpen = false;
    this.hasTriggered = false;
    this.delayTimer = null;
    
    // Elements
    this.overlay = this.querySelector('.newsletter-drawer__overlay');
    this.container = this.querySelector('.newsletter-drawer__container');
    this.closeBtn = this.querySelector('.newsletter-drawer__close');
    this.form = this.querySelector('.newsletter-drawer__form');
    this.emailInput = this.querySelector('input[name="email"]');
    this.submitBtn = this.querySelector('.newsletter-drawer__submit');
    this.submitText = this.querySelector('.newsletter-drawer__submit-text');
    this.submitLoading = this.querySelector('.newsletter-drawer__submit-loading');
    this.message = this.querySelector('.newsletter-drawer__message');
  }

  connectedCallback() {
    // Check if user has already closed the drawer
    if (this.getCookie(this.COOKIE_NAME)) {
      return;
    }

    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    // Bind event listeners
    this.closeBtn.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    
    // Prevent closing when clicking inside container
    this.container.addEventListener('click', (e) => e.stopPropagation());

    // Setup triggers
    if (this.exitIntent) {
      this.setupExitIntent();
    }

    // Setup delay trigger
    if (this.delay > 0) {
      this.delayTimer = setTimeout(() => {
        if (!this.hasTriggered) {
          this.open();
        }
      }, this.delay);
    }

    // Handle ESC key
    this.escHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escHandler);
  }

  setupExitIntent() {
    let exitIntentTriggered = false;

    this.exitIntentHandler = (e) => {
      // Desktop: trigger when mouse leaves viewport from top
      if (e.clientY <= 0 && !exitIntentTriggered && !this.hasTriggered) {
        exitIntentTriggered = true;
        this.open();
      }
    };

    // Only enable exit intent on desktop
    if (window.innerWidth >= 768) {
      document.addEventListener('mouseout', this.exitIntentHandler);
    }
  }

  open() {
    if (this.hasTriggered || this.isOpen) return;
    
    this.hasTriggered = true;
    this.isOpen = true;
    this.classList.add('active');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Clear delay timer if it exists
    if (this.delayTimer) {
      clearTimeout(this.delayTimer);
    }

    // Focus on email input after animation
    setTimeout(() => {
      this.emailInput?.focus();
    }, 600);
  }

  close() {
    this.isOpen = false;
    this.classList.remove('active');
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Set cookie
    this.setCookie(this.COOKIE_NAME, 'true', this.cookieDays);
  }

  async handleSubmit(e) {
    e.preventDefault();
    
    if (this.submitBtn.disabled) return;
    
    // Get form data
    const email = this.emailInput.value.trim();
    const preferences = Array.from(this.form.querySelectorAll('input[name="preference"]:checked'))
      .map(input => input.value);
    
    // Validate email
    if (!this.isValidEmail(email)) {
      this.showMessage('Please enter a valid email address', 'error');
      return;
    }

    // Show loading state
    this.setLoading(true);
    this.hideMessage();

    try {
      // Subscribe to Klaviyo
      await this.subscribeToKlaviyo(email, preferences);
      
      // Show success message
      this.showMessage('Thank you for subscribing! Check your email to confirm.', 'success');
      
      // Reset form
      this.form.reset();
      
      // Close drawer after delay
      setTimeout(() => {
        this.close();
      }, 3000);
      
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      this.showMessage('Something went wrong. Please try again.', 'error');
    } finally {
      this.setLoading(false);
    }
  }

  async subscribeToKlaviyo(email, preferences) {
    // Use Klaviyo legacy API (the working method)
    const formData = new FormData();
    formData.append('email', email);
    formData.append('g', this.KLAVIYO_LIST_ID);
    formData.append('$fields', '$source');
    formData.append('$source', 'Newsletter Drawer');
    
    if (preferences && preferences.length > 0) {
      preferences.forEach(pref => {
        formData.append('preferences[]', pref);
      });
    }

    await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
      method: 'POST',
      body: formData,
      mode: 'no-cors' // Required for Klaviyo cross-origin requests
    });

    // no-cors mode doesn't return response, but subscription is successful
    return { success: true };
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  setLoading(loading) {
    this.submitBtn.disabled = loading;
    if (loading) {
      this.submitText.style.display = 'none';
      this.submitLoading.style.display = 'block';
    } else {
      this.submitText.style.display = 'block';
      this.submitLoading.style.display = 'none';
    }
  }

  showMessage(text, type) {
    this.message.textContent = text;
    this.message.className = `newsletter-drawer__message ${type}`;
    this.message.style.display = 'block';
  }

  hideMessage() {
    this.message.style.display = 'none';
  }

  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }
    return null;
  }

  cleanup() {
    if (this.delayTimer) {
      clearTimeout(this.delayTimer);
    }
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
    }
    if (this.exitIntentHandler) {
      document.removeEventListener('mouseout', this.exitIntentHandler);
    }
    // Restore body scroll
    document.body.style.overflow = '';
  }
}

// Register the custom element
if (!customElements.get('newsletter-drawer')) {
  customElements.define('newsletter-drawer', NewsletterDrawer);
}
